<template>
  <asc-card class="asc-mxrec-recommendation--wrapper" flat>
    <template #title>Maintenance Recommendation</template>
    <div class="asc-mxrec-recommendation--content">
      <h1 class="asc-mxrec-recommendation--title">
        <v-clamp autoresize :max-lines="3" class="clamp">{{ title }}</v-clamp>
      </h1>
      <template v-if="!getCustomAlertMxRec">
        <asc-autocomplete-selector
          v-model="getStatusId"
          placeholder="Select a status"
          label="Status"
          class="mt-3 mb-2"
          no-array
          :readonly="isStatusReadonly"
          :items="getStatusOptions"
          :display-group-name="false"
          :sort-items="false"
        />
      </template>
      <template v-else>
        <asc-autocomplete-selector
          v-model="customAlertMxRecstatusId"
          label="Status"
          class="mt-3 mb-2"
          :sort-items="false"
          :items="mxRecStatusItems"
          :display-group-name="false"
          no-array
        ></asc-autocomplete-selector>
      </template>

      <asc-text-field-input
        label="Mx Rec Available Date"
        :value="getAvailableDate"
        :readonly="true"
        hide-details
      />
      <asc-text-field-input
        label="Mx Rec Source"
        :value="getSource"
        :readonly="true"
        hide-details
      />
    </div>
  </asc-card>
</template>

<script>
import AscCard from '../atoms/AscCard.vue'
import AscTextFieldInput from '../atoms/AscTextFieldInput.vue'
import AscAutocompleteSelector from '../molecules/AscAutocompleteSelector.vue'
import {
  AscMxRecPropsMixin,
  AscMxRecPermissionMixin,
} from '@/components/mixins'
import { COLLINS_PHM_TIER_3 } from '@/utilities/appConsts'

import { getFormattedDate, daysDifference } from '@/utilities/dates'

import VClamp from 'vue-clamp'

export default {
  name: 'AscMxRecRecommendationWrapper',

  components: {
    AscCard,
    AscTextFieldInput,
    AscAutocompleteSelector,
    VClamp,
  },
  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],
  props: {
    customAlertMxRecStatusOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    isIssueUpdater: {
      type: Boolean,
      default: false,
    },
    airlineTierTypeId: {
      type: Number,
      default: null,
    },
    data: {
      type: Object,
      default: () => {},
    },
  },

  computed: {
    title() {
      return this.data?.title
    },
    mxRecStatusItems() {
      const customAlertMxRecstatus = this.customAlertMxRecStatusOptions.map(
        item => {
          return {
            text: item.name,
            value: item.id,
            groupName: item.isTerminal ? 'closed' : 'airline',
          }
        },
      )
      return customAlertMxRecstatus
    },
    customAlertMxRecstatusId: {
      get() {
        return this.data?.status ? this.data.status.id : 0
      },
      set(val) {
        this.handleStatusChange(val)
      },
    },
    getStatusOptions: {
      get() {
        // need to increment the value for all the status options
        // since you can't use 0 for a value
        const incrementedStatusOptions = this.statusOptions
          // .filter(statusOptionObj => statusOptionObj.isExternal)
          .map(statusObj => {
            return {
              ...statusObj,
              value: statusObj.value + 1,
            }
          })

        const sortedStatusOptions = incrementedStatusOptions.sort((a, b) =>
          a.sortOrder > b.sortOrder ? 1 : -1,
        )
        return sortedStatusOptions
      },
    },
    getStatusId: {
      get() {
        const whichIndex = this.statusOptions.filter(statusOptionObj => {
          return statusOptionObj.id === this.data.status.id
        })
        if (whichIndex.length > 0) {
          // need to return the value incremented by 1 here since we had
          // to increment all the values of the statuses
          return whichIndex[0].value + 1
        } else {
          return null
        }
      },
      set(val) {
        const whichIndex = this.statusOptions.filter(statusOptionObj => {
          return statusOptionObj.value + 1 === val
        })
        if (whichIndex.length > 0) {
          this.handleStatusChange(whichIndex[0].id)
        } else {
          this.handleStatusChange(null)
        }
      },
    },
    getAvailableDate() {
      // Tier 3 or custom alert source then return created date as it wont have submitted date
      const date =
        this.airlineTierTypeId === COLLINS_PHM_TIER_3 ||
        this.data?.customAlertMxRec
          ? this.data?.createdDate
          : !this.isPreSubmittal
          ? this.data?.dateSubmitted
          : 'Not Submitted'

      const days =
        date && date !== 'Not Submitted'
          ? daysDifference(new Date(date), new Date())
          : null

      const daysAgo =
        date && date !== 'Not Submitted'
          ? days === 0
            ? 'Today'
            : days === 1
            ? '1 Day Ago'
            : `${days} Days Ago`
          : ''

      return date && date !== 'Not Submitted'
        ? `${getFormattedDate(date)} (${daysAgo})`
        : date === 'Not Submitted'
        ? date
        : 'N/A'
    },
    getSource() {
      return this.data?.manualIssueSource
        ? 'Manually Created'
        : this.data?.customAlertMxRec
        ? 'Custom Analytic'
        : 'PHM Analytic'
    },
    getCustomAlertMxRec() {
      return this.data?.customAlertMxRec
    },
    isStatusReadonly() {
      // only tier 3 issueupdater should be allowed to update
      return !(
        this.airlineTierTypeId === COLLINS_PHM_TIER_3 &&
        this.isMxRecEditPermission
      )
    },
  },
  methods: {
    handleStatusChange(val) {
      this.$emit('updateStatus', val)
    },
  },
}
</script>

<style lang="scss">
.asc-mxrec-recommendation {
  &--content {
    display: flex;
    flex-direction: column;
  }

  &--title {
    color: $primary-black;
    font-size: 25px;
    font-weight: 300;
    letter-spacing: 1.49px;
    line-height: 32px;
    text-transform: uppercase;

    .clamp {
      overflow-wrap: break-word;
      word-wrap: break-word;
      -ms-word-break: break-all;
      word-break: break-all;
      word-break: break-word;
      -ms-hyphens: auto;
      -moz-hyphens: auto;
      -webkit-hyphens: auto;
      hyphens: auto;
    }
  }
}
</style>
